/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC + composer boundary; see mem://preferences/lint-debt.md */
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import type {
  ContentVoiceProfile, ContentAvatar, ContentFramework, ContentFrameworkColumn,
  ContentFrameworkPrinciple, ContentFrameworkTopic, ContentChip, ContentSource,
  ContentBrief, ContentBriefReference, ContentBriefPlan, ContentBriefDraft,
  ContentDestinationFormat, ContentBriefPublication, ContentReferenceType,
  ReadwiseHighlightSummary, ExcerptReferenceSummary,
  ContentPublishingConnector, ContentPublishingDispatch,
  ComposerSnapshot, ContentReferenceGroup,
  ResolvedBriefReference, ContentBriefWithReferences,
  BriefPlanRecommendedReference, BriefPlanStructureItem,
  ContentBriefPlanParsed, ContentBriefDraftParsed, ContentBriefPublicationParsed,
} from "./types";
import {
  briefDraftsTable, briefPublicationsTable, getFriendlyMessage,
  asObjectArray, buildReferenceLookup, buildContentReferenceGroups,
  referenceKey, referenceTypeLabels,
} from "./_shared";

export const parseBriefPlan = (plan: ContentBriefPlan): ContentBriefPlanParsed => ({
  ...plan,
  recommendedReferencesParsed: asObjectArray<BriefPlanRecommendedReference>(plan.recommended_references),
  suggestedStructureParsed: asObjectArray<BriefPlanStructureItem>(plan.suggested_structure),
});

export const parseBriefDraft = (draft: ContentBriefDraft): ContentBriefDraftParsed => ({
  ...draft,
  contextSnapshotParsed:
    draft.context_snapshot && typeof draft.context_snapshot === "object" && !Array.isArray(draft.context_snapshot)
      ? (draft.context_snapshot as Record<string, Json | null>)
      : {},
});

export const getLatestPlanForBrief = (
  briefId: string,
  plans: ContentBriefPlan[],
  activePlanId?: string | null,
) => {
  const forBrief = plans.filter((plan) => plan.brief_id === briefId);
  // Explicit active plan (session switcher / URL hash) wins when it belongs
  // to this brief. Otherwise fall back to the newest non-archived row, then
  // (only if all are archived) the absolute newest — so callers always get
  // something as long as any plan exists.
  const active = activePlanId ? forBrief.find((p) => p.id === activePlanId) : null;
  const byNewest = [...forBrief].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const nonArchived = byNewest.filter(
    (p) => !(p as unknown as { archived_at?: string | null }).archived_at,
  );
  const latest = active ?? nonArchived[0] ?? byNewest[0] ?? null;

  return latest ? parseBriefPlan(latest) : null;
};


export const getLatestDraftForBrief = (briefId: string, drafts: ContentBriefDraft[]) => {
  const latest = drafts
    .filter((draft) => draft.piece_id === briefId)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

  return latest ? parseBriefDraft(latest) : null;
};

export const parseBriefPublication = (publication: ContentBriefPublication): ContentBriefPublicationParsed => ({
  ...publication,
  preparedMetadataParsed:
    publication.prepared_metadata && typeof publication.prepared_metadata === "object" && !Array.isArray(publication.prepared_metadata)
      ? (publication.prepared_metadata as Record<string, Json | null>)
      : {},
});

export const getPublicationsForDraft = (
  draftId: string,
  publications: ContentBriefPublication[],
): ContentBriefPublicationParsed[] =>
  publications
    .filter((publication) => publication.draft_id === draftId)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .map(parseBriefPublication);

export const getLatestPublicationForDraft = (
  draftId: string,
  destinationFormat: ContentDestinationFormat,
  publications: ContentBriefPublication[],
): ContentBriefPublicationParsed | null => {
  const latest = publications
    .filter((publication) => publication.draft_id === draftId && publication.destination_format === destinationFormat)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  return latest ? parseBriefPublication(latest) : null;
};

export const isPublicationOutdated = (
  brief: ContentBrief,
  draft: Pick<ContentBriefDraft, "updated_at"> | null,
  publication: Pick<ContentBriefPublication, "draft_updated_at_snapshot" | "piece_updated_at_snapshot"> | null,
) => {
  if (!publication) return false;
  if (publication.piece_updated_at_snapshot && new Date(brief.updated_at).getTime() > new Date(publication.piece_updated_at_snapshot).getTime()) {
    return true;
  }
  if (draft && publication.draft_updated_at_snapshot && new Date(draft.updated_at).getTime() > new Date(publication.draft_updated_at_snapshot).getTime()) {
    return true;
  }
  return false;
};

export const isBriefPlanOutdated = (brief: ContentBrief, plan: Pick<ContentBriefPlan, "brief_updated_at_snapshot"> | null) => {
  if (!plan?.brief_updated_at_snapshot) return false;
  return new Date(brief.updated_at).getTime() > new Date(plan.brief_updated_at_snapshot).getTime();
};

export const isBriefDraftOutdated = (
  brief: ContentBrief,
  draft: Pick<ContentBriefDraft, "piece_updated_at_snapshot"> | null,
) => {
  if (!draft?.piece_updated_at_snapshot) return false;
  return new Date(brief.updated_at).getTime() > new Date(draft.piece_updated_at_snapshot).getTime();
};

export const fetchComposerSnapshot = async (workspaceId: string): Promise<ComposerSnapshot> => {
  const [
    voiceProfileResult,
    avatarsResult,
    frameworksResult,
    columnsResult,
    principlesResult,
    topicsResult,
    chipsResult,
    sourcesResult,
    briefsResult,
    briefReferencesResult,
    briefPlansResult,
    briefDraftsResult,
    briefPublicationsResult,
    connectorsResult,
    dispatchesResult,
    highlightsResult,
    excerptsResult,
  ] = await Promise.all([
    // Voice profile / avatars / briefs: select metadata + previews (full text comes from get_decrypted_* RPCs below).
    supabase.from("content_voice_profiles").select("id, workspace_id, title, is_primary, metadata, voice_key_version, voice_mode_a_label, voice_mode_b_label, dual_voice_modes_enabled, created_at, updated_at, phrase_preferences:phrase_preferences_preview, avoid_list:avoid_list_preview, core_identity:core_identity_preview, voice_mode_a_notes:voice_mode_a_notes_preview, voice_mode_b_notes:voice_mode_b_notes_preview, sentence_architecture:sentence_architecture_preview, paragraph_flow:paragraph_flow_preview, vocabulary_fingerprint:vocabulary_fingerprint_preview, signature_phrases:signature_phrases_preview, verbal_markers:verbal_markers_preview, rhetorical_moves:rhetorical_moves_preview, primary_tone:primary_tone_preview, secondary_tone:secondary_tone_preview, formality_position:formality_position_preview, emotional_register:emotional_register_preview, tradition_synthesis:tradition_synthesis_preview, red_lines:red_lines_preview, common_misrepresentations:common_misrepresentations_preview, peak_author_examples:peak_author_examples_preview, authenticity_markers:authenticity_markers_preview, lexical_compliance:lexical_compliance_preview, building_block_protocols:building_block_protocols_preview").eq("workspace_id", workspaceId).order("is_primary", { ascending: false }).order("updated_at", { ascending: false }).limit(1).maybeSingle(),
    supabase.from("content_avatars").select("id, workspace_id, name, experience_level, pain_points, desires, tone_guidance, vocabulary_assumptions, messaging_posture, sort_order, metadata, summary_key_version, notes_key_version, voice_mode_a_weight, tone_adjustments, vocabulary_adjustments, created_at, updated_at, summary:summary_preview, notes:notes_preview").eq("workspace_id", workspaceId).order("sort_order", { ascending: true }).order("created_at", { ascending: true }),
    supabase.from("content_frameworks").select("*").eq("workspace_id", workspaceId).order("updated_at", { ascending: false }),
    supabase.from("content_framework_columns").select("*").eq("workspace_id", workspaceId).order("position", { ascending: true }),
    supabase.from("content_framework_principles").select("*").eq("workspace_id", workspaceId).order("position", { ascending: true }),
    supabase.from("content_framework_topics").select("*").eq("workspace_id", workspaceId).order("position", { ascending: true }),
    supabase.from("content_chips").select("*").eq("workspace_id", workspaceId).order("chip_type", { ascending: true }).order("label", { ascending: true }),
    // Source summary/notes are encrypted at rest; the list uses 280-char previews aliased to keep the ContentSource shape unchanged.
    supabase.from("content_sources").select("id, workspace_id, title, bucket, source_type, origin_label, status, key_version, body_encrypted, body_preview, summary:summary_preview, notes:notes_preview, summary_key_version, notes_key_version, metadata, created_at, updated_at").eq("workspace_id", workspaceId).order("updated_at", { ascending: false }),
    supabase.from("content_pieces").select("id, workspace_id, title, content_type, status, destination_defaults, approved_at, metadata, notes_key_version, prompt_key_version, created_at, updated_at, approved_brief_json, notes:notes_preview, prompt:prompt_preview").eq("workspace_id", workspaceId).order("updated_at", { ascending: false }),
    supabase.from("content_piece_references").select("*").eq("workspace_id", workspaceId).order("sort_order", { ascending: true }).order("created_at", { ascending: true }),
    supabase.from("content_brief_plans").select("*").eq("workspace_id", workspaceId).order("created_at", { ascending: false }),
    briefDraftsTable().select("id, workspace_id, piece_id, plan_id, requested_by_user_id, status, model, draft_title, draft_summary, draft_body_preview, revision_notes, error_message, context_snapshot, piece_updated_at_snapshot, plan_updated_at_snapshot, started_at, completed_at, created_at, updated_at, revision_kind, draft_doc, parent_draft_id, last_edited_by_user_id, sort_order").eq("workspace_id", workspaceId).order("sort_order", { ascending: true }).order("updated_at", { ascending: false, nullsFirst: false }).order("created_at", { ascending: false }),
    briefPublicationsTable().select("id, workspace_id, piece_id, draft_id, requested_by_user_id, status, destination_format, publish_type_key, model, prepared_title, prepared_excerpt, prepared_body_preview, prepared_metadata, revision_notes, error_message, draft_updated_at_snapshot, piece_updated_at_snapshot, context_snapshot, started_at, completed_at, created_at, updated_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }),
    (supabase as any).from("content_publishing_connectors").select("id, workspace_id, connector_type, display_name, status, config, created_by_user_id, last_used_at, last_error_message, created_at, updated_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }),
    (supabase as any).from("content_publishing_dispatches").select("*").eq("workspace_id", workspaceId).order("created_at", { ascending: false }).limit(200),
    supabase.from("readwise_highlights").select("id, workspace_id, highlight_text, book_title, book_author, note").eq("workspace_id", workspaceId).order("highlighted_at", { ascending: false, nullsFirst: false }).limit(200),
    // Excerpt picker uses notes only as a secondary signal — preview is enough and avoids a workspace-wide decrypt.
    supabase.from("excerpts").select("id, workspace_id, body_text, notes:notes_preview, library_items(title, display_title, author_text)").eq("workspace_id", workspaceId).order("updated_at", { ascending: false }).limit(300),
  ]);

  const error = [
    voiceProfileResult.error,
    avatarsResult.error,
    frameworksResult.error,
    columnsResult.error,
    principlesResult.error,
    topicsResult.error,
    chipsResult.error,
    sourcesResult.error,
    briefsResult.error,
    briefReferencesResult.error,
    briefPlansResult.error,
    briefDraftsResult.error,
    briefPublicationsResult.error,
    connectorsResult.error,
    dispatchesResult.error,
    highlightsResult.error,
    excerptsResult.error,
  ].find(Boolean);

  if (error) throw new Error(getFriendlyMessage(error, "Failed to load the content workspace"));

  // Decrypt full text via SECURITY DEFINER RPCs (workspace-gated). Editors need plaintext;
  // list/grid views above already use the *_preview aliases.
  const [
    decryptedDraftsResult,
    decryptedPubsResult,
    decryptedVoiceResult,
    decryptedAvatarsResult,
    decryptedBriefsResult,
  ] = await Promise.all([
    (supabase as any).rpc("get_decrypted_brief_drafts", { _workspace_id: workspaceId }),
    (supabase as any).rpc("get_decrypted_brief_publications", { _workspace_id: workspaceId }),
    (supabase as any).rpc("get_decrypted_voice_profiles", { _workspace_id: workspaceId }),
    (supabase as any).rpc("get_decrypted_content_avatars", { _workspace_id: workspaceId, _ids: null }),
    (supabase as any).rpc("get_decrypted_content_briefs", { _workspace_id: workspaceId, _ids: null }),
  ]);

  if (decryptedVoiceResult.error) {
    throw new Error(getFriendlyMessage(decryptedVoiceResult.error, "Failed to load the full Voice document"));
  }

  const draftBodyById = new Map<string, string | null>(
    ((decryptedDraftsResult.data ?? []) as Array<{ id: string; draft_body: string | null }>).map((r) => [r.id, r.draft_body]),
  );
  const pubBodyById = new Map<string, string | null>(
    ((decryptedPubsResult.data ?? []) as Array<{ id: string; prepared_body: string | null }>).map((r) => [r.id, r.prepared_body]),
  );
  const voiceTextById = new Map<string, Record<string, string | null>>(
    ((decryptedVoiceResult.data ?? []) as Array<{ id: string } & Record<string, string | null>>).map((r) => [r.id, r]),
  );
  const avatarTextById = new Map<string, { summary: string | null; notes: string | null }>(
    ((decryptedAvatarsResult.data ?? []) as Array<{ id: string; summary: string | null; notes: string | null }>).map((r) => [r.id, { summary: r.summary, notes: r.notes }]),
  );
  const briefTextById = new Map<string, { notes: string | null; prompt: string | null }>(
    ((decryptedBriefsResult.data ?? []) as Array<{ id: string; notes: string | null; prompt: string | null }>).map((r) => [r.id, { notes: r.notes, prompt: r.prompt }]),
  );

  // Overlay decrypted text onto the list rows (which currently hold previews).
  const voiceRow = voiceProfileResult.data as (ContentVoiceProfile & Record<string, string | null>) | null;
  const VOICE_DECRYPT_KEYS = [
    "avoid_list", "phrase_preferences",
    "core_identity", "voice_mode_a_notes", "voice_mode_b_notes",
    "sentence_architecture", "paragraph_flow", "vocabulary_fingerprint",
    "signature_phrases", "verbal_markers", "rhetorical_moves",
    "primary_tone", "secondary_tone", "formality_position", "emotional_register",
    "tradition_synthesis", "red_lines", "common_misrepresentations", "peak_author_examples",
    "authenticity_markers",
    "lexical_compliance",
    "building_block_protocols",
  ] as const;
  const voiceMerged = voiceRow
    ? (() => {
        const decrypted = voiceTextById.get(voiceRow.id);
        if (!decrypted) return voiceRow;
        const overlay: Record<string, string | null> = {};
        for (const key of VOICE_DECRYPT_KEYS) {
          overlay[key] = decrypted[key] ?? (voiceRow as Record<string, string | null>)[key] ?? null;
        }
        return { ...voiceRow, ...overlay } as ContentVoiceProfile;
      })()
    : null;
  const avatarsMerged = ((avatarsResult.data ?? []) as ContentAvatar[]).map((row) => {
    const decrypted = avatarTextById.get(row.id);
    return decrypted ? { ...row, summary: decrypted.summary ?? row.summary, notes: decrypted.notes ?? row.notes } : row;
  });
  const briefsMerged = ((briefsResult.data ?? []) as ContentBrief[]).map((row) => {
    const decrypted = briefTextById.get(row.id);
    return decrypted ? { ...row, notes: decrypted.notes ?? row.notes, prompt: decrypted.prompt ?? row.prompt } : row;
  });

  return {
    voiceProfile: voiceMerged,
    avatars: avatarsMerged,
    frameworks: (frameworksResult.data ?? []) as ContentFramework[],
    columns: (columnsResult.data ?? []) as ContentFrameworkColumn[],
    principles: (principlesResult.data ?? []) as ContentFrameworkPrinciple[],
    topics: (topicsResult.data ?? []) as ContentFrameworkTopic[],
    chips: (chipsResult.data ?? []) as ContentChip[],
    sources: (sourcesResult.data ?? []) as unknown as ContentSource[],
    briefs: briefsMerged,
    briefReferences: (briefReferencesResult.data ?? []) as ContentBriefReference[],
    briefPlans: (briefPlansResult.data ?? []) as ContentBriefPlan[],
    briefDrafts: ((briefDraftsResult.data ?? []) as any[]).map((row) => ({
      ...row,
      draft_body: draftBodyById.get(row.id) ?? row.draft_body_preview ?? null,
    })) as ContentBriefDraft[],
    briefPublications: ((briefPublicationsResult.data ?? []) as any[]).map((row) => ({
      ...row,
      prepared_body: pubBodyById.get(row.id) ?? row.prepared_body_preview ?? null,
    })) as ContentBriefPublication[],
    publishingConnectors: (connectorsResult.data ?? []) as ContentPublishingConnector[],
    publishingDispatches: (dispatchesResult.data ?? []) as ContentPublishingDispatch[],
    readwiseHighlights: (highlightsResult.data ?? []) as ReadwiseHighlightSummary[],
    excerpts: ((excerptsResult.data ?? []) as any[]).map((row) => ({
      id: row.id as string,
      workspace_id: row.workspace_id as string,
      body_text: (row.body_text as string) ?? "",
      notes: (row.notes as string | null) ?? null,
      author_name: (row.library_items?.author_text as string | null) ?? null,
      publication_title: (row.library_items?.display_title as string | null) ?? (row.library_items?.title as string | null) ?? null,
      library_title: (row.library_items?.display_title as string | null) ?? (row.library_items?.title as string | null) ?? null,
    })) as ExcerptReferenceSummary[],
  };
};

export const buildBriefReferenceCatalog = (snapshot: ComposerSnapshot): ContentReferenceGroup[] => buildContentReferenceGroups(snapshot);

export const fetchBriefReferenceCatalog = async (workspaceId: string): Promise<ContentReferenceGroup[]> => {
  const snapshot = await fetchComposerSnapshot(workspaceId);
  return buildContentReferenceGroups(snapshot);
};

export const resolveBriefsWithReferences = (snapshot: ComposerSnapshot): ContentBriefWithReferences[] => {
  const optionMap = buildReferenceLookup(snapshot);

  return snapshot.briefs.map((brief) => ({
    ...brief,
    references: snapshot.briefReferences
      .filter((reference) => reference.piece_id === brief.id)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((reference) => {
        const option = optionMap.get(referenceKey(reference.reference_type as ContentReferenceType, reference.reference_id));
        return {
          ...reference,
          label: option?.label ?? reference.label_snapshot ?? "Untitled reference",
          groupLabel: option?.groupLabel ?? referenceTypeLabels[reference.reference_type as ContentReferenceType] ?? "Reference",
          description: option?.description ?? null,
          colorKey: option?.colorKey ?? null,
        } satisfies ResolvedBriefReference;
      }),
  }));
};
